import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useMemo } from "react";
import {
  TreeNode,
  TreeNodeType,
  createNodesForSteps,
  createNodesForStepsGroupedByState,
} from "~/components/build/Show/components/Sidebar/TreeNode";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import {
  DisplayMode,
  useStateCounts,
  getRelevantSubFiltersForStep,
  filterJobs,
  FilterByOption,
  useFilterStore,
  filterSteps,
} from "~/components/build/Show/components/Sidebar/useFilterStore";
import { CommandJob } from "~/components/build/Show/lib/types";
import useExpandedIds from "~/components/build/Show/components/Sidebar/useExpandedIds";
import { useSteps } from "~/components/build/Show/lib/useSteps";

export interface VisibleNode extends TreeNode {
  depth: number;
  collapsibleState: CollapsibleState;
  parentPath: VisibleNode[];

  // Index of item in flattened tree.
  index: number;
}

/**
 * Returns a list of visible nodes based on expanded states and depth.
 */
export function visibleNodes(
  nodes: TreeNode[],
  collapsibleStates: Map<string, CollapsibleState>,
  depth: number,
  filters: FilterByOption[] = [],
  index = 0,
): VisibleNode[] {
  const result: VisibleNode[] = [];

  for (const node of nodes) {
    const visibleNode = {
      ...node,
      collapsibleState:
        node.childNodes === undefined
          ? CollapsibleState.None
          : collapsibleStates.get(node.id) || CollapsibleState.Collapsed,
      depth,
      parentPath: [],
      index,
    };

    result.push(visibleNode);
    index++;

    if (node.childNodes) {
      const childNodes = node.childNodes(visibleNode.collapsibleState, filters);
      const children = visibleNodes(childNodes, collapsibleStates, depth + 1, filters, index);

      children.forEach((child) => {
        child.parentPath = [visibleNode, ...child.parentPath];
        child.index = index++;
      });

      result.push(...children);
    }
  }

  return result;
}

export function filterStepJobs<T extends CommandJob>(jobs: T[], step, build): T[] {
  const filters = getRelevantSubFiltersForStep(step, build);
  return filterJobs(jobs, filters, build);
}

interface UseVisibleNodesProps {
  /**
   * Force display mode to be used, regardless of the user's preference.
   */
  forceDisplayMode?: DisplayMode;

  /**
   * Filters to apply to the list of steps.
   */
  filters?: FilterByOption[];
}

/**
 * Based on a filtered list of steps and display mode, return a list of visible tree nodes
 * respecting the expanded state of steps or state groups.
 */
export function useTreeNodes(props: UseVisibleNodesProps = {}): VisibleNode[] {
  const collapsibleStates = useExpandedIds();

  const { steps, stepJobs } = useSteps();
  const stateCounts = useStateCounts();
  const { build } = useBuild();

  const displayMode = useFilterStore(
    (state) => props.forceDisplayMode ?? (state.displayMode || DisplayMode.State),
  );

  const filteredSteps = useMemo(() => {
    const filtered = filterSteps(steps, props.filters || [], build);

    if (displayMode === DisplayMode.State) {
      return filtered.filter((step) => step.type !== "group" && step.type !== "wait");
    }
    return filtered;
  }, [steps, props.filters, build, displayMode]);

  const nodes = useMemo(() => {
    let nodes: TreeNode[];
    if (displayMode === DisplayMode.State) {
      nodes = createNodesForStepsGroupedByState(filteredSteps, build, stateCounts);
    } else {
      nodes = createNodesForSteps(filteredSteps, build);
    }

    return visibleNodes(nodes, collapsibleStates, 0, props.filters);
  }, [filteredSteps, stepJobs, build, displayMode, collapsibleStates]);

  return nodes;
}
